"use client";

import { createContext, useCallback, useContext, useMemo, useState } from "react";

export type AuthTab = "signin" | "register";

interface AuthModalState {
  isOpen: boolean;
  defaultTab: AuthTab;
  openAuthModal: (tab?: AuthTab) => void;
  closeAuthModal: () => void;
}

const AuthModalContext = createContext<AuthModalState | null>(null);

export function AuthModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [defaultTab, setDefaultTab] = useState<AuthTab>("signin");

  const openAuthModal = useCallback((tab: AuthTab = "signin") => {
    setDefaultTab(tab);
    setIsOpen(true);
  }, []);
  const closeAuthModal = useCallback(() => setIsOpen(false), []);

  const value = useMemo<AuthModalState>(
    () => ({ isOpen, defaultTab, openAuthModal, closeAuthModal }),
    [isOpen, defaultTab, openAuthModal, closeAuthModal],
  );

  return (
    <AuthModalContext.Provider value={value}>
      {children}
    </AuthModalContext.Provider>
  );
}

export function useAuthModal() {
  const ctx = useContext(AuthModalContext);
  if (!ctx) throw new Error("useAuthModal must be used within <AuthModalProvider>");
  return ctx;
}
